import { ACTION_TYPE } from "../core/redux/actionType";
import { hasTrippedBreaker, updateBreaker } from "../core/redux/api/circuitBreaker/module";
import { apiSearchLiftAccessRules } from "../core/redux/api/endpoints";
import { addError, addLoading, getLoading, removeLoadingAndError } from "../core/redux/api/module";
import { ApiReducer, makeApiAction, makeApiReducer, RsaaTypes } from "../core/redux/api/reduxStandardApiAction";
import { IAppState } from "../core/redux/IAppState";
import { AccessOverviewQuery, LiftAccessRuleSearchResult } from "./models";
import { assoc } from '../language/module';
import { normalizeFilter } from "./module";
import { HTTP_STATUS } from "../core/redux/api/reduxStandardApiAction";


export const KEY_ACTION = ACTION_TYPE.SEARCH_LIFT_ACCESS_RULES_REQUEST;

export const ACTIONS = [
  ACTION_TYPE.SEARCH_LIFT_ACCESS_RULES_REQUEST, 
  ACTION_TYPE.SEARCH_LIFT_ACCESS_RULES_RESPONSE,
  ACTION_TYPE.SEARCH_LIFT_ACCESS_RULES_ERROR,
];


/**
 * POST: Fetch Lift Access Rules with filter based on AccessOverviewQuery
 */
export const searchLiftAccessRulesAction = (filter: AccessOverviewQuery) => {

  return makeApiAction({
    endpoint: apiSearchLiftAccessRules(),
    method:   'POST',
    body:     JSON.stringify(filter),

    headers: (state: IAppState) => ({
      'content-type':  'application/json',
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),

    types: ACTIONS.map(type => ({type, meta: { filter }})) as RsaaTypes,

    /**
     * Only block if the same request is already running.
     * If the previous request finished (success or any error, including 422),
     * allow another one.
     */
    bailout: (state: IAppState) => {
      if (hasTrippedBreaker(state, KEY_ACTION)) return true;
      return !!getLoading(state, KEY_ACTION, KEY_ACTION); // block only while loading
    }
  });
};


const reducer: ApiReducer<LiftAccessRuleSearchResult> = (
  state,
  action,
  error,
  isLoading,
  payload,
) => {

  if (action.type === ACTION_TYPE.SEARCH_LIFT_ACCESS_RULES_REQUEST) {
    const filter = action.meta?.filter as AccessOverviewQuery | undefined;
    const normalized = filter ? normalizeFilter(filter) : undefined;

    const withLoading = addLoading(state, KEY_ACTION, KEY_ACTION);

    return {
      ...withLoading,
      liftAccess: {
        ...withLoading.liftAccess,
        liftAccessRuleSets: [],
        liftAccessRuleSetsLoadedAt: undefined,
        lastFilter: normalized,
      },
    };
  }

  if (isLoading) {
    return addLoading(state, KEY_ACTION, KEY_ACTION);
  }

  // Update circuit breaker only on REAL API failures
  const isRealError =
    !!error &&
    action.payload?.status !== HTTP_STATUS.UNPROCESSABLE_ENTITY;

  const withBreaker = updateBreaker(state, KEY_ACTION, isRealError);

  // Clear loading before returning any error
  if (error && action.payload.status === HTTP_STATUS.UNPROCESSABLE_ENTITY) {
    const cleared = removeLoadingAndError(withBreaker, KEY_ACTION, KEY_ACTION);

    return addError(cleared, KEY_ACTION, KEY_ACTION, "", {
      status: 422,
      resultLimit: action.payload.response?.limit,
      isResultLimitError: true
    });
  }

  if (error) {
    const cleared = removeLoadingAndError(withBreaker, KEY_ACTION, KEY_ACTION);
    return addError(cleared, KEY_ACTION, KEY_ACTION, error);
  }

  const noLoadingOrError = removeLoadingAndError(
    withBreaker,
    KEY_ACTION,
    KEY_ACTION,
  );

  return assoc(
    "liftAccess",
    {
      ...noLoadingOrError.liftAccess,
      liftAccessRuleSets: payload?.liftAccessRuleGroups ?? [],
      // Stamped where the results land, so anything reading them can tell how old
      // they are — including the bulk edit dialog, which composes from their rule ids.
      liftAccessRuleSetsLoadedAt: Date.now(),
    },
    noLoadingOrError,
  );
};


export const searchLiftAccessRulesReducer = makeApiReducer<LiftAccessRuleSearchResult>(ACTIONS, KEY_ACTION, reducer);